import { t } from "i18next";
import _ from "lodash";
import { DateTime, Info } from "luxon";
import { createContext, useContext, useEffect, useState } from "react";
import { generateBlocksByEquipmentTemplate, templates } from "../../../components/Templates/templates";
import { useFetch } from "../../../hooks/useFetch";
import { calculatePersonRow } from "../../../lib/calculations";
import { useAppStore } from "../../../stores/RootStore";
import {
  DataRow,
  EquipmentTemplate,
  EventsByPersonnelsResponseItemWithShiftAndPerson,
  PersonBlockFilters,
  // ReportTableData,
  Tab,
  useTabsStore
} from "../../../stores/TabsStore";
import { Equipment, EventsByPersonnelsResponseItem, Person } from "../../../types/types";
import { PersonsCategories } from "../filters/PersonsFilters";

let shiftCounter = 0;
let dataStorage: EventsByPersonnelsResponseItemWithShiftAndPerson[] = [];
let tempStorage: EventsByPersonnelsResponseItem[] = [];

let part = 0;
let equipmentAmount = 0;
let daysAmount = 0;
let shiftAmount = 0;
let CHUNK = 10;

let equipmentBatch = 0

interface ReportState {}

let ReportContext = createContext<ReportState>(null!);



// 0: "equipment_item_id"
// 1: "event_date"
// 2: "shift_date"
// 3: "start"
// 4: "start_ts"
// 5: "finish"
// 6: "finish_ts"
// 7: "duration"
// 8: "state"
// 9: "equipment_item_status"
// 10: "personnel_id"
// 11: "access_token_id"
// 11: "equipment_item_division_id"
// 12: "personnel_division_id"
// 13: "state_changed"
// 14: "status_changed"
// 15: "person_changed"
// 16: "hour"


export function ReportProvider({
  tab,
  children,
  addProcessedData,
}: {
  tab: Tab;
  children: React.ReactNode;
  addProcessedData: Function;
}) {
  const { shifts, equipments, persons } = useAppStore();
  const { addOptions, changeTabView, changeTabDataIsLoaded, addFilters, addBlocks, changeTabTitle } = useTabsStore();
  const [params, setParams] = useState(new URLSearchParams({}));

  const [{ response: response, isLoading: isLoading, error: error }, doFetchEvents] =
    useFetch(`/directories/ng_equipment_item_events/events_by_personnels?${params.toString()}`); 


  // Отправка первого запроса при сабмите формы
  useEffect(() => {
    if (!tab.defaultValues?.dates?.from || !tab.defaultValues?.dates?.to || !tab.defaultValues?.persons) {
      return;
    }
    if (tab.options?.tableData) return;
    changeTabDataIsLoaded(tab.id, false, true);
    equipmentAmount = tab.defaultValues.persons.length;
    daysAmount =
      tab.defaultValues?.period_type === "static"
        ? DateTime.fromISO(tab.defaultValues.dates.to.toISOString())
            .diff(DateTime.fromISO(tab.defaultValues.dates.from.toISOString()), "days")
            .toObject().days! + 1
        : tab.defaultValues?.dynamic_period!;

    shiftAmount = tab.defaultValues.shifts?.length!;
    if (daysAmount < 14) CHUNK = 10;
    else if (daysAmount < 30) CHUNK = 5;
    else CHUNK = 3;
    setParams(generateParams(tab, 0, part));
    doFetchEvents();
  }, [tab.defaultValues]);

  // Сохранение полученных данных и отправка дополнительного запроса
  useEffect(() => {
    if (!response?.data) return;
    const eventsData = response?.data as unknown as EventsByPersonnelsResponseItem[];
    tempStorage.push(...eventsData);
    addProcessedData(equipmentBatch);
    equipmentBatch = 0;

    if (CHUNK + part * CHUNK < equipmentAmount) {
      part = part + 1;
      setParams(generateParams(tab, shiftCounter, part));
      doFetchEvents();
    } else {
      part = 0;

      tempStorage.map((x) => {
        dataStorage.push({
          ...x,
          shift: tab.defaultValues?.shifts?.[shiftCounter]!,
          person: persons?.find(p => p.id === String(x.data[0][10]))!
        });
      });

      tempStorage = [];
      shiftCounter = shiftCounter + 1;

      if (shiftCounter < shiftAmount) {
        // Отправка запросов на получение данных по оборудованию за каждую выбранную смену
        setParams(generateParams(tab, shiftCounter, part));
        doFetchEvents();
      } else {
        shiftCounter = 0;
        let nodes = _.orderBy(buildData(dataStorage, equipments!, persons!), "at") as DataRow[];

        dataStorage = [];

        addOptions(tab.id, {...tab.options, report_type: "persons", params: params.toString(), reportData: nodes, tableData: []});

        // Категории для фильтров
        const categories: PersonsCategories = {
          unique_days: _.orderBy(_.uniqBy(nodes.map((n) => n.dateShift.start_day.toISODate()!).map(d =>  ({label: d, value: d})), "value"), "label"),
          unique_weekdays: Info.weekdays("short").map(wd => ({label: wd, value: wd})),
          unique_shifts: tab.defaultValues?.shifts?.map(s => ({label: s.name, value: s.id}))!,
          unique_equipment_models: _.uniqBy(equipments?.filter(e => tab.defaultValues?.equipments?.map(te => te.value).includes(String(e.id))).map(e => ({label: e.attributes.equipment_model_name!, value: String(e.attributes.equipment_model_name)}))!, "value"),
          unique_equipments: _.uniqBy(equipments?.filter(e => tab.defaultValues?.equipments?.map(te => te.value).includes(String(e.id))).map(e => ({label: e.attributes.name, value: String(e.id)}))!, "value"),
          unique_equipment_types: _.uniqBy(equipments?.filter(e => tab.defaultValues?.equipments?.map(te => te.value).includes(String(e.id))).map(e => ({label: e.attributes.equipment_type_name, value: String(e.attributes.equipment_type_id)}))!, "value"),
          unique_persons: _.orderBy(_.uniqBy(nodes.map((n) => ({label: `${n.person?.attributes.last_name} ${n.person?.attributes.first_name} ${n.person?.attributes.middle_name}`, value: String(n.person?.id)})), "value"), "label"),
          unique_posts: _.orderBy(_.uniqBy(nodes.map((n) => (n.person?.attributes.post_name ? {label: n.person?.attributes.post_name!, value: String(n.person?.attributes.post_name!)} : {label: t('no_post'), value: "no_post"})), "value"), "label"),
          unique_card_types: _.uniq(nodes.map(n => n.person?.attributes.access_tokens).flat().map(at => at?.access_token_type!)).map(i =>  ({label: t(i), value: i})),
        };

        if (categories) addFilters(tab.id, { ...tab.options?.filters, categories: categories });

        // Категории для фильтров в блоках
        const filtersForBlocks: PersonBlockFilters = {
          days: _.uniq(nodes.map((n) => n.dateShift.start_day.toISODate()!)),
          weekdays: _.uniq(nodes.map((n) => String(n.dateShift.start_day.weekday))),
          shifts: _.uniq(nodes.map((n) => n.dateShift.id)),
          equipments: _.uniq(nodes.map((n) => n.equipment.id)),
          persons: _.uniq(
            nodes
              .map(
                (n) =>
                  n.interactionIds?.map(
                    (i) => {
                      if(Number(i) === 0) {
                        return `${t('without_person')}`
                      } else {
                        let person = persons?.find(p => Number(p.id) === Number(i))
                        return `${person?.attributes.last_name} ${person?.attributes.first_name} ${person?.attributes.access_token_id}`
                      }
                    }
                  )!
              )
              .flat()
          ),
        };

        if (!tab.options?.blocks) {
          const template = templates.find((t) => t.slug === tab.template) as unknown as EquipmentTemplate;
          changeTabTitle(tab.id, tab.title);
          addBlocks(tab.id, generateBlocksByEquipmentTemplate(filtersForBlocks, template, shifts, equipments, persons));
        }

        changeTabDataIsLoaded(tab.id, true, false);
        changeTabView(tab.id, "report", "summary", _.uniq(nodes.map((n) => n.person?.id!)));
      }
    }
  }, [response?.data]);


  let value = {};
  return <ReportContext.Provider value={value}>{children}</ReportContext.Provider>;
}

export function useReport() {return useContext(ReportContext)}

// Генерация параметров для запросов
const generateParams = (tab: Tab, iteration: number, part: number, exclude: boolean = false) => {
  let dates =
    tab.defaultValues?.period_type === "static"
      ? { from: tab.defaultValues?.dates?.from?.toISOString(), to: tab.defaultValues?.dates?.to?.toISOString() }
      : {
          from: DateTime.fromSeconds(
            Math.round(DateTime.now().minus({ day: 1 }).toSeconds()) - tab.defaultValues?.dynamic_period! * 24 * 60 * 60
          ).toISO(),
          to: DateTime.fromSeconds(Math.round(DateTime.now().minus({ day: 1 }).toSeconds())).toISO(),
        };

  let shift = tab.defaultValues?.shifts?.[iteration];
  let newUsageParams = new URLSearchParams({});

  newUsageParams.append("from", dates?.from!);
  newUsageParams.append("to", dates?.to!);
  newUsageParams.append("day_time_from", shift?.start_at.toFormat("HH:mm:ss")!);
  newUsageParams.append("day_time_to", shift?.end_at.toFormat("HH:mm:ss")!);
  equipmentBatch = 0;
  for (let i = 0 + part * CHUNK; i < CHUNK + CHUNK * part && i < tab.defaultValues?.persons?.length!; i++) {
    let el = tab.defaultValues?.persons![i]!;
    newUsageParams.append("personnel_ids[]", el.value);
    equipmentBatch = equipmentBatch + 1;
  }
  return newUsageParams;
};


// Построение данных
function buildData(data: EventsByPersonnelsResponseItemWithShiftAndPerson[], equipments: Equipment[], persons: Person[]) {
  let nodes: DataRow[] = [];
  let counter = 1;

  for (let row of data) {
    let eq = equipments.find((e) => e.id === String(row.equipment_item_id))!;

    nodes.push({
      id: counter,
      hidden: false,
      ...calculatePersonRow(row.at, row.data, row.shift, eq, row.person)
    });
    counter = counter + 1;
  }
  return nodes;
}